/**
 * CommandsTableRow - Linha da tabela de comandos salvos
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Avatar,
  Typography,
  Chip,
  Checkbox,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { formatBoolean } from '../../common/util/formatter';
import { prefixString } from '../../common/util/stringUtils';
import CollectionActions from './CollectionActions';

// Helper para cores do status SMS - USA TOKENS DINÂMICOS
const getSmsStatusColors = (textChannel, tokens) => {
  if (textChannel) {
    return {
      text: tokens?.semantic?.success,
      bg: tokens?.semantic?.successBg,
      border: `${tokens?.semantic?.success}40`,
    };
  }
  return {
    text: tokens?.text?.muted,
    bg: tokens?.surface?.glass,
    border: tokens?.border?.subtle,
  };
};

const CommandsTableRow = memo(({
  row,
  onRefresh,
  limitCommands,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const smsColors = getSmsStatusColors(row.textChannel, tokens);

  // Cor do avatar baseada no tipo de comando
  const avatarColor = tokens?.accent?.primary;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors,
        '&:hover': {
          backgroundColor: tokens?.surface?.hover,
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 0.5, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 0.5, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            border: `1px solid ${avatarColor}40`,
          }}
        >
          <TerminalIcon sx={{ fontSize: 18, color: avatarColor }} />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 0.5, width: 70 }}>
        <Typography variant="caption" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Description */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 250 }}>
          {row.description || '-'}
        </Typography>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ py: 0.5 }}>
        <Chip
          label={t(prefixString('command', row.type))}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 500,
            bgcolor: tokens?.semantic?.infoBg,
            color: tokens?.semantic?.info,
            border: `1px solid ${tokens?.semantic?.info}30`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* SMS Status */}
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Chip
          label={formatBoolean(row.textChannel, t)}
          size="small"
          sx={{
            minWidth: 50,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: smsColors.bg,
            color: smsColors.text,
            border: `1px solid ${smsColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Actions */}
      {!limitCommands && (
        <TableCell align="right" sx={{ py: 0.5 }}>
          <CollectionActions
            itemId={row.id}
            editPath="/settings/command"
            endpoint="commands"
            setTimestamp={onRefresh}
          />
        </TableCell>
      )}
    </TableRow>
  );
});

CommandsTableRow.displayName = 'CommandsTableRow';

export default CommandsTableRow;
